import { toCustomerError } from '../utils/customerError';

import { API_BASE_URL } from '../config/apiEndpoints';

class VenueListsApiService {
  normalizeListCollection(payload) {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.items)) return payload.items;
    if (Array.isArray(payload?.lists)) return payload.lists;
    if (Array.isArray(payload?.venue_lists)) return payload.venue_lists;
    if (Array.isArray(payload?.data)) return payload.data;
    return [];
  }

  getAuthHeaders() {
    const token = localStorage.getItem('access_token');
    const headers = {
      'Content-Type': 'application/json'
    };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    return headers;
  }

  async parseResponse(response) {
    if (!response.ok) {
      let detail = '';
      try {
        const errorData = await response.json();
        detail = errorData?.detail || errorData?.message || errorData?.error || '';
      } catch (parseError) {
        detail = '';
      }
      const detailText = String(detail || '');
      const expiredToken =
        response.status === 401 ||
        /token has expired|expired token|not authorised|unauthorized|jwt/i.test(detailText);

      if (expiredToken) {
        throw new Error('SESSION_EXPIRED');
      }

      throw new Error(toCustomerError(detailText, { status: response.status }));
    }

    const data = await response.json();
    return data?.data ?? data;
  }

  async listVenueLists({ scope = 'mine' } = {}) {
    const params = new URLSearchParams();
    if (scope) params.set('scope', scope);
    const response = await fetch(`${API_BASE_URL}/venue-lists?${params.toString()}`, {
      headers: this.getAuthHeaders()
    });
    const data = await this.parseResponse(response);
    return this.normalizeListCollection(data);
  }

  async createVenueList(payload) {
    const response = await fetch(`${API_BASE_URL}/venue-lists`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(payload)
    });
    return this.parseResponse(response);
  }

  async getVenueList(listId) {
    const response = await fetch(`${API_BASE_URL}/venue-lists/${listId}`, {
      headers: this.getAuthHeaders()
    });
    return this.parseResponse(response);
  }

  async updateVenueList(listId, payload) {
    const response = await fetch(`${API_BASE_URL}/venue-lists/${listId}`, {
      method: 'PATCH',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(payload)
    });
    return this.parseResponse(response);
  }

  async addVenueListItem(listId, payload) {
    const response = await fetch(`${API_BASE_URL}/venue-lists/${listId}/items`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(payload)
    });
    return this.parseResponse(response);
  }

  async removeVenueListItem(listId, itemId) {
    const response = await fetch(`${API_BASE_URL}/venue-lists/${listId}/items/${itemId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders()
    });
    return this.parseResponse(response);
  }

  async inviteVenueListMembers(listId, invites) {
    const response = await fetch(`${API_BASE_URL}/venue-lists/${listId}/invites`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ invites })
    });
    return this.parseResponse(response);
  }

  async updateMemberRole(listId, memberId, role) {
    const response = await fetch(`${API_BASE_URL}/venue-lists/${listId}/members/${memberId}`, {
      method: 'PATCH',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ role })
    });
    return this.parseResponse(response);
  }

  async removeMember(listId, memberId) {
    const response = await fetch(`${API_BASE_URL}/venue-lists/${listId}/members/${memberId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders()
    });
    return this.parseResponse(response);
  }

  async voteOnVenueListItem(listId, payload) {
    const response = await fetch(`${API_BASE_URL}/venue-lists/${listId}/votes`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(payload)
    });
    return this.parseResponse(response);
  }

  async getVenueListResults(listId) {
    const response = await fetch(`${API_BASE_URL}/venue-lists/${listId}/results`, {
      headers: this.getAuthHeaders()
    });
    return this.parseResponse(response);
  }

  async lockVenueList(listId) {
    const response = await fetch(`${API_BASE_URL}/venue-lists/${listId}/lock`, {
      method: 'POST',
      headers: this.getAuthHeaders()
    });
    return this.parseResponse(response);
  }

  async acceptInvite(token) {
    const response = await fetch(`${API_BASE_URL}/venue-lists/invites/accept`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ token })
    });
    return this.parseResponse(response);
  }
}

const venueListsApiService = new VenueListsApiService();
export default venueListsApiService;
